import { Button } from '../../design/components'
import type { LiveSessionPhase } from './types'
import styles from './live.module.css'

export interface PhaseBarProps {
  phase: LiveSessionPhase
  encounterType: string
  canControl: boolean
  busy: boolean
  onChange: (phase: LiveSessionPhase) => void
}

/**
 * Which half of the evening this is, and the one control that changes it.
 *
 * **At the top of the stage, above everything it governs.** The phase decides what the whole
 * middle of the screen *is* — faces, or a map and a turn order — so it is not a setting tucked in
 * a tools tab; it is the heading the rest of the screen is read under. Players see the heading
 * and no button, which is the same rule as everywhere else on this screen: the player build binds
 * no control at all rather than binding a disabled one.
 *
 * Nothing here decides for itself. There is no "you have set an order, so the encounter has
 * started" — that is a call somebody makes at a table, and the app's job is to have a button ready
 * when they make it.
 */
export function PhaseBar({ phase, encounterType, canControl, busy, onChange }: PhaseBarProps) {
  const inEncounter = phase === 'InEncounter'

  return (
    <div className={styles.phaseBar}>
      <span className={styles.phaseState}>
        <span
          className={`${styles.phaseDot} ${inEncounter ? styles.phaseDotLive : ''}`}
          aria-hidden="true"
        />
        <span>
          {inEncounter ? `In the ${encounterType.toLowerCase()}` : 'Before it starts'}
          <span className={styles.rowMeta}>
            {inEncounter
              ? ' · the map and the turn order'
              : ' · who is here, and who the table has met'}
          </span>
        </span>
      </span>

      {canControl && (
        <Button
          variant={inEncounter ? 'secondary' : 'primary'}
          disabled={busy}
          onClick={() => onChange(inEncounter ? 'PreEncounter' : 'InEncounter')}
        >
          {/* The label says what pressing it does, not what state the session is in — the state is
              already written to the left of it, and a toggle labelled with its current value is
              the oldest ambiguity in interface design. */}
          {inEncounter ? 'Step back out' : 'Start the encounter'}
        </Button>
      )}
    </div>
  )
}
